// Shared query config + variable builders for the TWG recs query. Used by both the
// client hook and the SSR preloader so they issue byte-identical variables (needed
// for the client's cache-first read to hit the SSR-primed entry).

// TWG relationship for related Confluence content (spec: CA3).
const TWG_RELATIONSHIP = 'confluence_content_has_related_confluence_content';

// Buffer above the 6-card grid limit so permission-filtered nodes don't drop us
// below the "min 2 cards" render invariant.
export const MAX_TWG_RECS = 12;

// AGG needs an `X-Query-Context` site-ARI header for `graphStore_*` fields.
export const buildTwgQueryContext = (cloudId: string): string =>
	`ari:cloud:platform::site/${cloudId}`;

// Matches @atlassian/ari's Confluence{Page,BlogPost}Ari, inlined to avoid the
// dependency. Callers guard with isTwgSupportedContentType, so contentType is
// 'page' or 'blogpost'; blogposts need the `blogpost/` segment or the graph lookup
// resolves to a non-existent node.
export const buildTwgContentAri = (
	cloudId: string,
	contentId: string,
	contentType: string,
): string =>
	`ari:cloud:confluence:${cloudId}:${contentType === 'blogpost' ? 'blogpost' : 'page'}/${contentId}`;

// No cypher `LIMIT`: the relationship returns a small, capped set already, and we
// filter non-current content client-side after hydration (see `extractTwgRecContent`),
// so limiting in the query would just shrink the pool the FE filter can draw from.
// The GraphQL `first` arg still bounds the edges returned.
export const buildTwgRecsCypher = (pageAri: string): string =>
	[
		`MATCH (page {ari: "${pageAri}"})-[:${TWG_RELATIONSHIP}]->(entity)`,
		`RETURN DISTINCT entity AS ari`,
	].join('\n');

/** Content types the TWG recommendations query supports. */
export const isTwgSupportedContentType = (contentType: string | undefined): boolean =>
	contentType === 'page' || contentType === 'blogpost';
